/* eslint-disable no-shadow */
import React, { useEffect, useState, lazy, Suspense, useMemo } from 'react';
import { connect, useDispatch } from 'react-redux';
import classnames from 'classnames';
import styled, { keyframes } from 'styled-components';
import { TiStarOutline, TiStarFullOutline } from 'react-icons/ti';
import { FaTrophy } from 'react-icons/fa';
// import API from '@aws-amplify/api';
import { Link } from 'react-router-dom';
import { FiCheckCircle, FiCircle } from 'react-icons/fi';

import {
   setStage,
   submitAnswer,
   tutorialAction,
   setSelectedModules,
   preSubmitAnswer,
} from './actions';
import { exerciseStages } from '../shared/constants';
import { assetsUrl, authStatuses } from '../util/constants';
import { updateSubscription } from '../app/actions';
import { Modal } from '../components/Modal';
import {
   ChallengePrompt,
   Tutorial,
   TutorialWrapper,
} from '../tutorial/Tutorial';
import { AdvanceExerciseButton } from './AdvanceExerciseButton';
import { ErrorBoundary } from '../ErrorBoundary';
import { ElementLoading } from '../components/ElementLoading';
import { Button } from '../components/Button';
import { AuthConditionalRender } from '../auth/AuthConditionalRender';
import { Authenticator } from '../auth/Authenticator';
import { RoundLogo } from '../branding/RoundLogo';
import { INTERVAL_EXERCISE_TYPE } from './exerciseTypes';
import { Form } from '../auth/authenticatorComponents/AuthenticatorForm';
import { audioContext } from '../webAudio/audioContext';

// const Subscribe = lazy(() => import('./Subscribe'));

const SelectModulesStyles = styled.div`
   ul {
      max-height: 300px;
      overflow: auto;
   }

   input {
      width: 0;
      margin: 0;
      opacity: 0;
   }

   li:not(:last-child) {
      margin-bottom: 1.5em;
   }

   li label {
      display: grid;
      grid-template-columns: 2em 1fr;
      grid-gap: 1em;
      cursor: pointer;
   }

   li label:hover {
      color: var(--brand-orange);
   }

   li h2 {
      font-size: 1.25em;
      font-family: var(--heading-font);
      margin-bottom: 0.5em;
   }

   && li p {
      margin-bottom: 0;
      font-size: 1em;
      line-height: 1.6;
   }

   .select-modules-button-wrapper {
      text-align: center;
      margin-top: 2em;
   }
`;

const popIn = keyframes`
   from {
      transform: scale(0.01);
   }

   85% {
      transform: scale(1.125);

   }

   to {
      transform: scale(1);
   }
`;

const fadeIn = keyframes`
   from {
      opacity: 0;
   }


   to {
      opacity: 1;
   }
`;

const loadingFade = keyframes`
   from {
      opacity: 1;
   }

   50% {
      opacity: 0.25;
   }

   to {
      opacity: 1;
   }
`;

const ExerciseControlsStyles = styled.div`
   /* a {
      color: var(--brand-darker-green);
   }
   a:hover,
   a:focus-visible,
   a:active {
      color: var(--brand-orange);
   }

   @media (prefers-color-scheme: dark) {
      a:not(.button) {
         color: var(--brand-light-orange);
      }

      a:hover,
      a:focus-visible,
      a:active {
         color: var(--brand-orange);
      }
   } */

   .button-link {
      margin-top: 1em;
      text-decoration: none;
   }

   .button-text-loading {
      animation: ${loadingFade} 2.5s ease-in-out infinite;
   }

   button {
      min-width: 8em;
   }

   .button-dismiss {
      padding: 0;
      font-size: 0.9em;
   }

   .exercise-control-signup-wrapper {
      width: var(--modal-content-width);
   }

   .exercise-control-signup-logo {
      display: block;
      margin: 0 auto 2em;
      width: 125px;
      position: relative;
   }

   .exercise-control-modal-content {
      margin: 0 auto;
      width: var(--modal-content-width);
      font-family: var(--content-font);

      h1 {
         font-family: var(--heading-font);
         font-size: 1.4em;
         margin-bottom: 1.5em;
         text-align: center;
      }

      p {
         font-size: 1.25em;
         margin-bottom: 1.5em;
         line-height: 1.6;
      }

      .exercise-control-button-wrapper {
         text-align: center;
      }

      .exercise-control-score {
         font-family: var(--heading-font);
         text-align: center;
         margin-bottom: 1em;

         @media (min-height: 400px) {
            margin-bottom: revert;
         }
      }
   }

   @media (max-height: 650px) {
      .ei-tutorial-modal .ei-modal-content {
         display: grid;
         grid-template-columns: 1fr 1fr;
         grid-gap: 1em;
      }
   }

   .exercise-control-trophy-icon {
      margin: 2em auto;
      width: 5em;
      height: 5em;
      display: block;
   }

   .exercise-control-challenge-stars {
      margin: 10% auto 0;
      white-space: nowrap;
      width: 50%;

      @media (min-height: 340px) {
         width: 75%;
      }

      @media (min-height: 375px) {
         width: 100%;
      }

      @media (min-height: 400px) {
         margin-bottom: 1em;
      }

      & > div {
         position: relative;

         display: inline-block;
         width: calc(100% / 3);
         transform: scale(1.2);
      }
      &:not(.no-stars) > div:not(.full) {
         animation: ${fadeIn} 1s ease-in-out both;
      }

      svg {
         width: 100%;
         height: auto;
      }

      .full svg {
         animation: ${popIn} 0.75s ease-in-out both;
      }
      .full:first-child svg {
         animation-delay: 0.25s;
      }
      .full:nth-child(2) svg {
         animation-delay: 0.5s;
      }
      .full:last-child svg {
         animation-delay: 0.75s;
      }

      div:nth-child(2) {
         transform: scale(1.44) translateY(-1em);
      }

      svg:first-child {
         color: var(--semantic-warn);
         opacity: 0;
      }

      .full svg:first-child {
         opacity: 1;
      }

      svg:last-child {
         position: absolute;
         inset: 0;
      }

      @media (prefers-color-scheme: dark) {
         svg:last-child {
            color: var(--semantic-warn-interact);
         }
      }
   }

   .exercise-control-score-button-wrapper {
      display: grid;
      grid-gap: 0.5em;
      width: min(100%, 13em);
      margin: 1em auto 0;

      @media (min-height: 400px) {
         margin-top: 2em;
      }

      .button-dismiss {
         margin-top: 1em;
      }
   }

   .below-interface-button-wrapper {
      text-align: center;
   }

   .exercise-control-modal-content .exercise-control-level-wrapper {
      font-family: var(--heading-font);
      margin-bottom: 0.75em;
      text-align: center;
      margin-top: -1.25em;
   }

   .exercise-control-level-aux-name {
      display: inline-block;
      margin-top: 0.75em;
   }

   .exercise-control-level {
      display: flex;
      justify-content: center;
      font-size: 1em;

      @media (min-height: 400px) {
         font-size: 1.5em;
      }
   }

   .exercise-control-level-numerator {
      font-size: 2.5em;
   }
   .exercise-control-level-denominator {
      font-size: 0.75em;
      vertical-align: sub;
   }

   .exercise-control-level-button-wrapper {
      margin: 1.5em auto 0;
      display: grid;
      grid-gap: 0.5em;
      width: min(100%, 13em);

      .button-dismiss {
         margin-top: 1em;
      }
   }

   ${TutorialWrapper} {
      padding-right: 0;
      padding-bottom: 0;
      padding-left: 0;

      .tutorial-button-wrapper {
         min-height: 46px;
      }
   }

   .exercise-control-modal-form {
      margin-bottom: 3em;

      input {
         width: 100%;
         border-radius: 5px;
         padding: 10px;
         border: 2px solid;
         color: var(--text-color);
         font-weight: bold;
         font-size: 1.1em;
      }

      input::placeholder {
         color: var(--brand-darker-green);
         opacity: 0.5;
      }

      @media (min-width: 500px) {
         input {
            width: 100%;
         }
      }

      input:hover,
      input:focus,
      input:active {
         outline: none;
         border-color: var(--hover-color);
      }

      @media (prefers-color-scheme: dark) {
         input {
            background-color: var(--brand-lighter-green);
            color: var(--brand-darker-green);
            border-color: var(--brand-green);
         }

         input::placeholder {
            color: var(--brand-darker-green);
            opacity: 0.5;
         }
      }

      .exercise-control-subscribe-error {
         color: var(--semantic-error);
      }
   }
`;

const SessionAccuracy = ({ currentExercise, exerciseStage }) => {
   const [cachedScore, setCachedScore] = useState(null);

   useEffect(() => {
      setCachedScore(
         currentExercise.levelSelect
            ? currentExercise.levelSelect.lastAverageScore
            : currentExercise.progress.sessionHistory[
                 currentExercise.progress.sessionHistory.length - 1
              ]?.score
      );
      // ony want to update if exercise stage changes
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [exerciseStage]);

   return (
      <>
         {cachedScore === 100 && (
            <FaTrophy className="exercise-control-trophy-icon" />
         )}
         <p className="exercise-control-score">Accuracy: {cachedScore}%</p>
      </>
   );
};

const LevelUpDisplay = ({ currentExercise, exerciseStage, isModule }) => {
   const [cachedData, setCachedData] = useState({});

   useEffect(() => {
      setCachedData({
         title: currentExercise.exerciseDocument.title,
         currentLevel: currentExercise.progress.currentLevel,
         levelCount: currentExercise.exerciseDocument.levelCount,
      });
      // ony want to update if exercise stage changes
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [exerciseStage]);

   return (
      <p className="exercise-control-level-wrapper">
         {isModule && (
            <span className="exercise-control-level-aux-name">
               {cachedData.title}
            </span>
         )}
         <span className="exercise-control-level">
            <span>
               <span className="exercise-control-level-numerator">
                  {cachedData.currentLevel}
               </span>
               <span className="exercise-control-level-denominator">
                  /{cachedData.levelCount}
               </span>
            </span>
         </span>
      </p>
   );
};

const SubscribePrompt = ({
   dispatchAction,
   isSubscribed,
   levelSelect,
   currentExercise,
}) => {
   const [showSubscribe, setShowSubscribe] = useState(false);
   const [plans, setPlans] = useState(null);
   useEffect(() => {
      // API.get('app', '/purchase/plans').then(setPlans);
   }, []);

   if (isSubscribed) {
      return (
         <div className="exercise-control-modal-content">
            <h1>Success!</h1>
            <p>
               A subscription has been added to your account. Thank you for your
               support!
            </p>

            <div className="exercise-control-button-wrapper">
               <AdvanceExerciseButton className="button-cta">
                  Continue
               </AdvanceExerciseButton>
            </div>
         </div>
      );
   }

   if (showSubscribe) {
      return (
         <div className="exercise-control-modal-content">
            <h1>Subscribe</h1>
            <p>
               After submitting this form, you will recevie an email with a free
               link to unlock the remaining levels.
            </p>
            <Form
               method="POST"
               action="https://earinstructor.activehosted.com/proc.php"
            >
               <input
                  type="hidden"
                  name="u"
                  value={currentExercise.exerciseDocument.acFormId}
               />
               <input
                  type="hidden"
                  name="f"
                  value={currentExercise.exerciseDocument.acFormId}
               />
               <input type="hidden" name="s" />
               <input type="hidden" name="c" value="0" />
               <input type="hidden" name="m" value="0" />
               <input type="hidden" name="act" value="sub" />
               <input type="hidden" name="v" value="2" />
               <input
                  name="name"
                  autoComplete="given-name"
                  placeholder="First Name"
                  required
                  autoFocus
               />
               <input
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder="Email"
                  required
               />
               <Button className="button-cta" type="submit">
                  Subscribe
               </Button>
            </Form>
         </div>
         // <ErrorBoundary>
         //    <Suspense fallback={<ElementLoading />}>
         //       <div className="exercise-control-modal-content">
         //          <Subscribe
         //             onSubscribe={(token) =>
         //                dispatchAction(false, updateSubscription(token))
         //             }
         //             plans={plans}
         //          />
         //       </div>
         //    </Suspense>
         // </ErrorBoundary>
      );
   }

   return (
      <div className="exercise-control-modal-content">
         {/* <h1>Subscribe?</h1>
         {levelSelect ? (
            <p>
               This level is not available without a subscription to Ear
               Instructor. Please subscribe to continue.
            </p>
         ) : (
            <p>
               You have reached the highest level that is available without a
               subscription. Please subscribe if you want to advance to the next
               level.
            </p>
         )}

         <div className="exercise-control-level-button-wrapper">
            <Button
               className="button-cta"
               type="button"
               onClick={() => setShowSubscribe(true)}
            >
               Let&apos;s Do It!
            </Button>

            {!levelSelect && (
               <AdvanceExerciseButton className="button-dismiss">
                  Not Now
               </AdvanceExerciseButton>
            )}
         </div> */}
         <h1>Subscribe?</h1>
         {levelSelect ? (
            <>
               <p>
                  This level is not available without a FREE subscription to Ear
                  Instructor.
               </p>
               <p>Subscribe now to continue.</p>
            </>
         ) : (
            <>
               <p>
                  You have reached the highest level that is available without a
                  FREE subscription to Ear Instructor.
               </p>
               <p>Subscribe now to advance to the next level.</p>
            </>
         )}

         <div className="exercise-control-level-button-wrapper">
            <Button
               className="button-cta"
               type="button"
               onClick={() => setShowSubscribe(true)}
            >
               Let&apos;s Do It!
            </Button>

            {!levelSelect && (
               <AdvanceExerciseButton className="button-dismiss">
                  Not Now
               </AdvanceExerciseButton>
            )}
         </div>
      </div>
   );
};

const ViewScore = ({
   score,
   onCompareAnswer,
   highlighted,
   hideActionButtons,
}) => {
   let emptyStarDelay = 0.5;
   if (score >= 80) {
      emptyStarDelay = 1.25;
   } else if (score >= 60) {
      emptyStarDelay = 1;
   }
   return (
      <div className="exercise-control-modal-content">
         <div
            className={classnames('exercise-control-challenge-stars', {
               'no-stars': score < 60,
            })}
         >
            <div
               className={classnames({ full: score >= 60 })}
               style={{ animationDelay: `${emptyStarDelay}s` }}
            >
               <TiStarFullOutline size="7.5em" />
               <TiStarOutline size="7.5em" />
            </div>
            <div
               className={classnames({ full: score >= 80 })}
               style={{ animationDelay: `${emptyStarDelay}s` }}
            >
               <TiStarFullOutline size="7.5em" />
               <TiStarOutline size="7.5em" />
            </div>
            <div
               className={classnames({ full: score === 100 })}
               style={{ animationDelay: `${emptyStarDelay}s` }}
            >
               <TiStarFullOutline size="7.5em" />
               <TiStarOutline size="7.5em" />
            </div>
         </div>
         <p className="exercise-control-score">Score: {score}</p>
         {!hideActionButtons && (
            <div className="exercise-control-score-button-wrapper">
               <Button
                  type="button"
                  onClick={onCompareAnswer}
                  className={classnames({
                     highlighted: highlighted.compareAnswer,
                  })}
               >
                  Compare Answer
               </Button>
               <AdvanceExerciseButton>Next Challenge</AdvanceExerciseButton>
            </div>
         )}
      </div>
   );
};

ViewScore.defaultProps = {
   highlighted: {},
};

class CacheChildrenWhenNull extends React.Component {
   shouldComponentUpdate(nextProps) {
      const childrenIsArray = Array.isArray(nextProps.children);
      if (
         (childrenIsArray &&
            nextProps.children.filter((child) => child).length > 0) ||
         (!childrenIsArray && nextProps.children)
      ) {
         return true;
      }
      return false;
   }

   render() {
      // eslint-disable-next-line react/destructuring-assignment
      return this.props.children;
   }
}

const SignUpPrompt = ({
   signUpRequired,
   isTutorial,
   dispatchAction,
   levelSelect,
}) => {
   let message = 'Would you like to create an account to save your progress?';
   if (signUpRequired) {
      message =
         'Please create an account so that your progress can be saved before continuing to the next level.';
   }
   if (levelSelect) {
      message =
         'You must have an Ear Instructor account to access this level. Please sign up or log in to continue.';
   }

   return (
      <div>
         <p>{message}</p>
         <div className="exercise-control-level-button-wrapper">
            <Button
               className="button-cta"
               type="button"
               onClick={() => {
                  dispatchAction(isTutorial, setStage(exerciseStages.signUp));
               }}
            >
               Let&apos;s Do It!
            </Button>

            {!signUpRequired && (
               <AdvanceExerciseButton className="button-dismiss">
                  Not Now
               </AdvanceExerciseButton>
            )}
         </div>
      </div>
   );
};

const NonModalWrapper = ({ challengePrompt, children }) => {
   if (challengePrompt) {
      return (
         <ChallengePrompt challengePrompt={challengePrompt}>
            {children}
         </ChallengePrompt>
      );
   }
   return <p className="below-interface-button-wrapper">{children}</p>;
};

const SelectModules = ({
   dispatchAction,
   isTutorial,
   exerciseId,
   exerciseName,
}) => {
   const [selectingState, setSelectingState] = useState('pending');
   const [modules, setModules] = useState(null);
   const [selectedIds, setSelectedIds] = useState({});

   useEffect(() => {
      fetch(`${assetsUrl}/public/exercise/${exerciseId}/exerciseInfo.json`)
         .then((res) => res.json())
         .then(({ modulesList }) => setModules(modulesList));
   }, [exerciseId]);

   const selectedArray = Object.entries(selectedIds)
      .filter(([, selected]) => selected)
      .map(([moduleId]) => moduleId);

   if (selectingState === 'pending') {
      return (
         <>
            <p>
               Congratulations! You have completed the core content of{' '}
               {exerciseName}.
            </p>
            <p>
               You can continue to practice by selecting modules that allow you
               to focus on specific skills.
            </p>
            <div className="exercise-control-button-wrapper">
               <Button
                  className="button-cta"
                  type="button"
                  onClick={() => setSelectingState('selecting')}
               >
                  Let&apos;s do it!
               </Button>
            </div>
         </>
      );
   }

   if (selectingState === 'selecting' && modules == null) {
      return <ElementLoading />;
   }

   if (selectingState === 'selecting') {
      return (
         <SelectModulesStyles>
            <ul>
               {modules.map(({ id, title, description }) => (
                  <li key={id}>
                     <label htmlFor={`module-${id}`}>
                        <div>
                           <input
                              type="checkbox"
                              id={`module-${id}`}
                              onChange={(e) =>
                                 setSelectedIds((ids) => ({
                                    ...ids,
                                    [id]: e.target.checked,
                                 }))
                              }
                           />
                           {selectedIds[id] ? (
                              <FiCheckCircle size="2em" />
                           ) : (
                              <FiCircle size="2em" />
                           )}
                        </div>
                        <div>
                           <h2>{title}</h2>
                           <p>{description}</p>
                        </div>
                     </label>
                  </li>
               ))}
            </ul>

            <div className="select-modules-button-wrapper">
               <Button
                  className="button-cta"
                  type="button"
                  disabled={selectedArray.length === 0}
                  onClick={() => {
                     setSelectingState('selected');
                     dispatchAction(
                        isTutorial,
                        setSelectedModules(selectedArray)
                     );
                  }}
               >
                  Confirm Selection
               </Button>
            </div>
         </SelectModulesStyles>
      );
   }

   return (
      <div>
         <p>
            Great! New challenges will now feature a random module from your
            selection.
         </p>
         <div className="exercise-control-button-wrapper">
            <AdvanceExerciseButton className="button-cta">
               Start Next Challenge
            </AdvanceExerciseButton>
         </div>
      </div>
   );
};

const isStage = (requiredStage, exerciseStage, tutorialStage) =>
   exerciseStage === requiredStage ||
   (exerciseStage === exerciseStages.tutorial &&
      tutorialStage === requiredStage);

const containsStage = (stageOptions, exerciseStage, tutorialStage) =>
   stageOptions.reduce(
      (hasMatched, stage) =>
         hasMatched || isStage(stage, exerciseStage, tutorialStage),
      false
   );

const mapStateToProps = ({
   activeExercise,
   tutorial,
   appData: { userData, permissions },
}) => {
   const tutorialStep =
      tutorial?.tutorialDocument?.sections?.[tutorial.section]?.steps?.[
         tutorial.step
      ];
   const subData = permissions?.subscriptionAndPurchases?.subscription;

   let isSubscribed = false;
   if (subData) {
      if (
         new Date(subData.expiration) > Date.now() &&
         subData.includes.includes('exercise')
      ) {
         isSubscribed = true;
      }
   }
   return {
      activeExercise,
      tutorialStep,
      isLoggedIn: userData.status === authStatuses.authenticated,
      // isLoggedIn: true,
      isSubscribed,
   };
};

const ExerciseControls = connect(mapStateToProps)(
   ({ activeExercise, tutorialStep, isLoggedIn, isSubscribed }) => {
      const [showModal, setShowModal] = useState(false);

      const weeklyChallengeId = useMemo(
         () => new URLSearchParams(window.location.search).get('wcid'),
         []
      );

      useEffect(() => {
         if (
            activeExercise?.exerciseDocument &&
            containsStage(
               [
                  exerciseStages.levelUp,
                  exerciseStages.selectModules,
                  exerciseStages.sessionOverview,
                  exerciseStages.subscribePrompt,
                  exerciseStages.viewScore,
                  exerciseStages.welcome,
                  exerciseStages.signUp,
                  exerciseStages.signUpPrompt,
               ],
               activeExercise?.currentStage,
               tutorialStep?.exerciseStage
            )
         ) {
            setShowModal(true);
         } else {
            setShowModal(false);
         }
      }, [
         activeExercise?.currentStage,
         activeExercise?.exerciseDocument,
         tutorialStep?.exerciseStage,
      ]);

      const dispatch = useDispatch();

      if (activeExercise == null) {
         return null;
      }

      const dispatchAction = (isTutorial, action) =>
         dispatch(isTutorial ? tutorialAction(action) : action);

      const isTutorial =
         activeExercise.currentStage === exerciseStages.tutorial;

      const currentExercise = activeExercise.currentModuleId
         ? activeExercise.modules[activeExercise.currentModuleId]
         : activeExercise;

      return (
         <ExerciseControlsStyles>
            <Modal
               display={showModal}
               className={classnames({
                  'ei-tutorial-modal':
                     isTutorial && tutorialStep?.exerciseStage,
               })}
            >
               <CacheChildrenWhenNull>
                  {isStage(
                     exerciseStages.selectModules,
                     activeExercise.currentStage,
                     tutorialStep?.exerciseStage
                  ) && (
                     <div className="exercise-control-modal-content">
                        <h1>Select Modules</h1>
                        <SelectModules
                           exerciseId={activeExercise.exerciseDocument.id}
                           exerciseName={activeExercise.exerciseDocument.title}
                           dispatchAction={dispatchAction}
                           isTutorial={isTutorial}
                        />
                     </div>
                  )}
                  {isStage(
                     exerciseStages.signUpPrompt,
                     activeExercise.currentStage,
                     tutorialStep?.exerciseStage
                  ) && (
                     <div className="exercise-control-modal-content">
                        <h1>Sign Up?</h1>
                        <SignUpPrompt
                           signUpRequired
                           dispatchAction={dispatchAction}
                           isTutorial={isTutorial}
                           levelSelect={activeExercise.levelSelect != null}
                        />
                     </div>
                  )}
                  {isStage(
                     exerciseStages.subscribePrompt,
                     activeExercise.currentStage,
                     tutorialStep?.exerciseStage
                  ) && (
                     <SubscribePrompt
                        dispatchAction={dispatchAction}
                        isTutorial={isTutorial}
                        isSubscribed={isSubscribed}
                        levelSelect={activeExercise.levelSelect != null}
                        currentExercise={activeExercise}
                     />
                  )}
                  {isStage(
                     exerciseStages.signUp,
                     activeExercise.currentStage,
                     tutorialStep?.exerciseStage
                  ) && (
                     <>
                        <AuthConditionalRender
                           allowedStatuses={[
                              authStatuses.unauthenticated,
                              authStatuses.loading,
                           ]}
                        >
                           <div className="exercise-control-signup-wrapper">
                              <RoundLogo className="exercise-control-signup-logo" />
                              <Authenticator initialView="signUp" />
                           </div>
                        </AuthConditionalRender>
                        <AuthConditionalRender
                           allowedStatuses={[authStatuses.authenticated]}
                        >
                           <div className="exercise-control-modal-content">
                              <RoundLogo className="exercise-control-signup-logo" />
                              <h1>Welcome!</h1>
                              <p>
                                 I hope that Ear Instructor will be a valuable
                                 resource as you continue to learn and practice
                                 music production.
                              </p>
                              <p>
                                 <strong>Thank you for joining!</strong>
                              </p>
                              <div className="exercise-control-level-button-wrapper">
                                 <AdvanceExerciseButton className="button-cta">
                                    Next Challenge
                                 </AdvanceExerciseButton>
                              </div>
                           </div>
                        </AuthConditionalRender>
                     </>
                  )}
                  {isStage(
                     exerciseStages.levelUp,
                     activeExercise.currentStage,
                     tutorialStep?.exerciseStage
                  ) && (
                     <div className="exercise-control-modal-content">
                        <h1>Level Up!</h1>
                        <LevelUpDisplay
                           currentExercise={currentExercise}
                           isModule={activeExercise.currentModuleId}
                           exerciseStage={
                              tutorialStep?.exerciseStage ||
                              activeExercise.exerciseStage
                           }
                        />
                        {activeExercise.nextConfig &&
                           activeExercise.nextConfig.remainingSessionCount ===
                              0 &&
                           !activeExercise.nextConfig.onboarding && (
                              <div className="exercise-control-level-button-wrapper">
                                 <Button
                                    className="button-cta"
                                    as={Link}
                                    to={activeExercise.nextConfig.path}
                                 >
                                    Next Exercise
                                 </Button>
                              </div>
                           )}
                        {(activeExercise.nextConfig == null ||
                           activeExercise.nextConfig.remainingSessionCount >
                              0) &&
                           !isLoggedIn && (
                              <SignUpPrompt
                                 signUpRequired={
                                    currentExercise.progress.currentLevel >
                                    currentExercise.exerciseDocument
                                       .maxUnauthenticatedLevel
                                 }
                                 isTutorial={isTutorial}
                                 dispatchAction={dispatchAction}
                              />
                           )}
                        {(activeExercise.nextConfig == null ||
                           activeExercise.nextConfig.remainingSessionCount >
                              0) &&
                           isLoggedIn && (
                              <div className="exercise-control-level-button-wrapper">
                                 <AdvanceExerciseButton className="button-cta" />
                              </div>
                           )}
                     </div>
                  )}
                  {isStage(
                     exerciseStages.sessionOverview,
                     activeExercise.currentStage,
                     tutorialStep?.exerciseStage
                  ) && (
                     <div className="exercise-control-modal-content">
                        {(activeExercise.nextConfig == null ||
                           activeExercise.nextConfig.remainingSessionCount >
                              0) && (
                           <>
                              <h1>Session Complete!</h1>
                              <SessionAccuracy
                                 currentExercise={currentExercise}
                                 exerciseStage={
                                    tutorialStep?.exerciseStage ||
                                    activeExercise.currentStage
                                 }
                              />
                           </>
                        )}
                        {activeExercise.nextConfig &&
                           activeExercise.nextConfig.remainingSessionCount ===
                              0 &&
                           !activeExercise.nextConfig.onboarding && (
                              <>
                                 <h1>Session Complete!</h1>
                                 <SessionAccuracy
                                    currentExercise={currentExercise}
                                    exerciseStage={
                                       tutorialStep?.exerciseStage ||
                                       activeExercise.currentStage
                                    }
                                 />
                                 <div className="exercise-control-button-wrapper">
                                    {activeExercise.hasLeveledUp ? (
                                       <AdvanceExerciseButton className="button-cta" />
                                    ) : (
                                       <Button
                                          className="button-cta"
                                          as={Link}
                                          to={activeExercise.nextConfig.path}
                                       >
                                          Next Exercise
                                       </Button>
                                    )}
                                 </div>
                              </>
                           )}
                        {activeExercise.nextConfig &&
                           activeExercise.nextConfig.remainingSessionCount ===
                              0 &&
                           activeExercise.nextConfig.onboarding && (
                              <>
                                 <h1>Great Work!</h1>
                                 <p>
                                    You can continue learning this skill after
                                    joining{' '}
                                    <span style={{ display: 'inline-block' }}>
                                       Ear Instructor.
                                    </span>{' '}
                                    But for now, let's try out a different
                                    exercise.
                                 </p>
                                 <div className="exercise-control-level-button-wrapper">
                                    <Button
                                       className="button-cta"
                                       as={Link}
                                       to={activeExercise.nextConfig.path}
                                    >
                                       Next Exercise
                                    </Button>
                                    <Button
                                       className="button-link"
                                       as="a"
                                       href="lol"
                                       target="_blank"
                                    >
                                       I want to join now!
                                    </Button>
                                 </div>
                              </>
                           )}

                        {weeklyChallengeId != null ? (
                           <div className="exercise-control-score-button-wrapper">
                              <Button
                                 as={Link}
                                 to={`/app/weekly-challenge?selected=${weeklyChallengeId}`}
                              >
                                 Select New Challenge
                              </Button>
                              <AdvanceExerciseButton>
                                 Play Again
                              </AdvanceExerciseButton>
                           </div>
                        ) : (
                           !(
                              activeExercise.nextConfig &&
                              activeExercise.nextConfig
                                 .remainingSessionCount === 0
                           ) && (
                              <div className="exercise-control-button-wrapper">
                                 {!isLoggedIn &&
                                    !activeExercise.hasLeveledUp && (
                                       <SignUpPrompt
                                          isTutorial={isTutorial}
                                          dispatchAction={dispatchAction}
                                       />
                                    )}
                                 {(isLoggedIn ||
                                    activeExercise.hasLeveledUp) && (
                                    <AdvanceExerciseButton className="button-cta" />
                                 )}
                              </div>
                           )
                        )}
                     </div>
                  )}
                  {isStage(
                     exerciseStages.viewScore,
                     activeExercise.currentStage,
                     tutorialStep?.exerciseStage
                  ) && (
                     <ViewScore
                        hideActionButtons={isTutorial}
                        score={
                           isTutorial
                              ? tutorialStep?.exerciseData?.score
                              : activeExercise.scores[
                                   activeExercise.scores.length - 1
                                ]
                        }
                        highlighted={{
                           compareAnswer:
                              tutorialStep?.highlightedControls?.[
                                 'exercise-compareAnswer'
                              ],
                        }}
                        onCompareAnswer={() =>
                           dispatchAction(
                              isTutorial,
                              setStage(exerciseStages.compareAnswer)
                           )
                        }
                     />
                  )}
                  {isStage(
                     exerciseStages.welcome,
                     activeExercise.currentStage,
                     tutorialStep?.exerciseStage
                  ) && (
                     <div className="exercise-control-modal-content">
                        <h1>{activeExercise.exerciseDocument?.title}</h1>
                        <p>{activeExercise.exerciseDocument?.description}</p>
                        <div className="exercise-control-button-wrapper">
                           <AdvanceExerciseButton
                              className="button-cta"
                              onClick={() => audioContext.resume()}
                           >
                              Start Practice
                           </AdvanceExerciseButton>
                        </div>
                     </div>
                  )}
               </CacheChildrenWhenNull>
               {tutorialStep && (
                  <CacheChildrenWhenNull>
                     {tutorialStep?.exerciseStage ===
                        exerciseStages.viewScore && <Tutorial viewScore />}
                  </CacheChildrenWhenNull>
               )}
            </Modal>

            {containsStage(
               [exerciseStages.challenge, exerciseStages.compareAnswer],
               activeExercise.currentStage,
               tutorialStep?.exerciseStage
            ) && (
               <NonModalWrapper challengePrompt={activeExercise.prompt}>
                  {isStage(
                     exerciseStages.challenge,
                     activeExercise.currentStage,
                     tutorialStep?.exerciseStage
                  ) && (
                     // Should this be an advance exercise button?
                     <Button
                        type="button"
                        onClick={() =>
                           dispatchAction(
                              isTutorial,
                              !isTutorial &&
                                 activeExercise.type === INTERVAL_EXERCISE_TYPE
                                 ? preSubmitAnswer()
                                 : submitAnswer()
                           )
                        }
                        className={classnames({
                           'button-cta': activeExercise.prompt,
                           highlighted:
                              tutorialStep?.highlightedControls?.[
                                 'exercise-submitAnswer'
                              ],
                        })}
                     >
                        Submit Answer
                     </Button>
                  )}
                  {isStage(
                     exerciseStages.compareAnswer,
                     activeExercise.currentStage,
                     tutorialStep?.exerciseStage
                  ) && (
                     <AdvanceExerciseButton
                        delay
                        className={classnames({
                           'button-cta': activeExercise.prompt,
                           highlighted:
                              tutorialStep?.highlightedControls?.[
                                 'exercise-nextChallenge'
                              ],
                        })}
                     >
                        Next Challenge
                     </AdvanceExerciseButton>
                  )}
               </NonModalWrapper>
            )}
         </ExerciseControlsStyles>
      );
   }
);

export { ExerciseControls };
